import { date } from 'quasar'
import Activity from 'src/models/activity/Activity'
import ScheduleSlot from 'src/models/activity-schedule/ScheduleSlot'
import ActivitySession from 'src/models/activity-session/ActivitySession'
import Model, { ID, Integer, IsoDate, IsoDateTime, MODEL_LEVEL } from 'src/models/model'
import { validateDayNumber } from 'src/models/schedule/BookingWeek'
import SiteSchedule from 'src/models/schedule/SiteSchedule'
import { START_HOUR, timeToMinutes } from 'src/models/schedule/TimeSlot'

export const ACTIVITY_SCHEDULE_VIEW_TYPES = {
  ACTIVITY_SPACE: 'activity-space',
  DAY: 'day'
}

export const SCHEDULE_MODE_TYPES = {
  PUBLISHED: 'published',
  ACTUAL: 'actual'
}

const SNAP_TO_MINUTES_DEFAULT = 5

export default class ActivitySchedule extends Model {
  static name = 'ActivitySchedule'

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.NonFilmProgramming]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      startDate: {
        type: IsoDate,
        sortable: true
      },
      period: {
        type: String,
        output: true
      },
      weekName: {
        type: String,
        output: true
      },
      defaultSiteSchedule: {
        type: SiteSchedule,
        output: true
      },
      activitySessions: {
        type: [ActivitySession],
        default: () => { return [] },
        allowDestroy: true
      },
      // LOCALS
      selectedActivityForPaint: {
        type: Activity,
        default: null,
        local: true
      },
      scheduleSlotGhost: {
        type: ScheduleSlot,
        default: null,
        local: true
      },
      activitySpaceFilter: {
        type: ID,
        default: null,
        local: true
      },
      dayFilter: {
        type: Integer,
        default: 5, // Default to Friday, TODO: use default from circuit
        local: true
      },
      by: {
        type: String,
        default: ACTIVITY_SCHEDULE_VIEW_TYPES.DAY,
        local: true
      },
      scheduleMode: {
        type: String,
        local: true,
        default: SCHEDULE_MODE_TYPES.ACTUAL
      },
      pixelsPerMinute: {
        type: Number,
        default: 1,
        local: true
      },
      snapTo: {
        type: Integer,
        default: SNAP_TO_MINUTES_DEFAULT,
        local: true
      },
      overrideLock: {
        type: Boolean,
        default: false,
        local: true
      },
      fullSchedule: {
        type: [ScheduleSlot],
        default: () => { return [] },
        local: true
      }
    }

    this.computed = {
      fullScheduleData () {
        if (!this.activitySessions) return ''
        // Does it make sense to return a hash key here instead?
        return `${_.map(this.activitySessions.data, activitySession => activitySession)}`
      },
      visibleSchedule () {
        const schedule = this.scheduleMode === SCHEDULE_MODE_TYPES.PUBLISHED ? _.filter(this.fullSchedule, ({ activitySession }) => activitySession.published) : this.fullSchedule

        return _.filter(schedule, ({ activitySession }) => {
          if (this.by === ACTIVITY_SCHEDULE_VIEW_TYPES.ACTIVITY_SPACE && this.activitySpaceFilter !== null && activitySession.activitySpace.id !== this.activitySpaceFilter) {
            return false
          }
          if (this.by === ACTIVITY_SCHEDULE_VIEW_TYPES.DAY && this.dayFilter !== null && +activitySession.dayNumber !== this.dayFilter) {
            return false
          }

          return true
        })
      },
      daysOfWeekNumbers () {
        if (this.dayFilter === null) {
          return this.configuration.currentCircuit.daysOfWeekNumbers
        }
        return [this.dayFilter]
      },
      startDayNumber () {
        return this.startDate.getDay()
      },
      selected () {
        return _.filter(this.visibleSchedule, { selected: true })
      },
      selectedWithEditableTime () {
        if (_.some(this.selected, ({ activitySession }) => activitySession && activitySession.loading)) return []

        return _.filter(this.selected, ({ activitySession }) => activitySession && !activitySession.loading && (this.overrideLock || (!activitySession.published && !activitySession.hasTicketsSold)))
      },
      canPublishActivitySessions () {
        return _.some(this.selected, ({ activitySession }) => !activitySession.published)
      },
      canUnpublishActivitySessions () {
        return _.some(this.selected, ({ activitySession }) => activitySession.published)
      },
      // Don't allow time alignment if multiple activity sessions selected in the same activity space and day
      canAlignTime () {
        return this.selectedWithEditableTime.length && _.uniqBy(this.selectedWithEditableTime, ({ activitySession }) => `${activitySession.activitySpace.id},${activitySession.dayNumber}`).length === this.selectedWithEditableTime.length
      },
      resourceGraphHeight () {
        return 0
      },
      filteredActivitySpaces () {
        if (this.by !== ACTIVITY_SCHEDULE_VIEW_TYPES.ACTIVITY_SPACE || this.activitySpaceFilter === null) {
          return this.configuration.activitySpaces.data
        }

        return _.filter(this.configuration.activitySpaces.data, activitySpace => activitySpace.id === this.activitySpaceFilter)
      }
    }

    this.watchers = [
      [
        function () { return [this.selectedActivityForPaint, this.id] },
        function () {
          if (
            (!this.selectedActivityForPaint && this.scheduleSlotGhost) ||
              (this.selectedActivityForPaint && this.scheduleSlotGhost && this.selectedActivityForPaint.id !== this.scheduleSlotGhost.activitySession.activity.id)
          ) {
            this.destroyScheduleSlotGhost()
          }
        }
      ],
      [
        function () { return [this.fullScheduleData, this.id] },
        function () {
          _.each(this.fullSchedule, scheduleSlot => {
            scheduleSlot.destroy()
          })

          let schedule

          if (!this.activitySessions) {
            schedule = []
          } else {
            schedule = _.map(this.activitySessions.data, activitySession => new ScheduleSlot({ activitySession }))
          }

          this.fullSchedule = schedule
        }
      ]
    ]

    this.paginate = true
  }

  alignStart () {
    const activitySessionToAlignFrom = _.sortBy(this.selectedWithEditableTime, ({ activitySession }) => activitySession.startTime)[0].activitySession
    _.each(this.selectedWithEditableTime, ({ activitySession }) => {
      activitySession.startTime = activitySessionToAlignFrom.startTime
      activitySession.endTime = activitySessionToAlignFrom.endTime
    })
  }

  alignEnd () {
    const activitySessionToAlignFrom = _.sortBy(this.selectedWithEditableTime, ({ activitySession }) => -(activitySession.endTime))[0].activitySession
    _.each(this.selectedWithEditableTime, ({ activitySession }) => {
      activitySession.startTime = activitySessionToAlignFrom.startTime
      activitySession.endTime = activitySessionToAlignFrom.endTime
    })
  }

  snapToMinutes (minutes, scheduleSlots) {
    if (!scheduleSlots) {
      scheduleSlots = this.selectedWithEditableTime
    }

    if (!minutes) {
      minutes = this.snapTo || 5
    }
    _.each(scheduleSlots, ({ activitySession }) => {
      const currentMinutes = timeToMinutes(activitySession.startTime)
      const roundedMinutes = Math.round(currentMinutes / minutes) * minutes
      activitySession.addMinutes(roundedMinutes - currentMinutes)
    })
  }

  activitySpaceIndex (activitySpaceId) {
    return _.indexOf(_.map(this.filteredActivitySpaces, 'id'), +activitySpaceId)
  }

  dayIndex (dayNumber) {
    return _.indexOf(this.daysOfWeekNumbers, +dayNumber)
  }

  clearCurrentSelection () {
    _.each(this.visibleSchedule, scheduleSlot => { scheduleSlot.currentSelection = false })
  }

  clearFinalSelection () {
    _.each(this.visibleSchedule, scheduleSlot => { scheduleSlot.finalSelection = false })
  }

  clearSelection () {
    this.clearCurrentSelection()
    this.clearFinalSelection()
  }

  selectBy ({ activitySpaceId = null, dayNumber = null, activityId = null }) {
    this.clearSelection()
    _(this.visibleSchedule).filter(({ activitySession }) => {
      return (activitySpaceId === null || activitySession.activitySpace.id === activitySpaceId) &&
        (dayNumber === null || activitySession.dayNumber === dayNumber) &&
        (activityId === null || activitySession.activity.id === activityId)
    }).each(scheduleSlot => { scheduleSlot.finalSelection = true })
  }

  copyPriorDay (dayNumber) {
    const priorDayNumber = validateDayNumber(dayNumber - 1)

    _(this.fullSchedule)
      .filter(({ activitySession }) => activitySession.dayNumber === priorDayNumber)
      .each(({ activitySession }) => {
        const newActivitySession = activitySession.copy()
        newActivitySession.published = false
        newActivitySession.hasTicketsSold = false
        newActivitySession.setDayNumber(dayNumber, this.startDayNumber)
        this.activitySessions.add(newActivitySession)
      })
  }

  removeSelectedActivitySessions () {
    _.each(this.selected, ({ activitySession }) => {
      if (activitySession.published || activitySession.hasTicketsSold) return

      this.activitySessions.remove(activitySession)
    })
  }

  removeDisplayedActivitySessions () {
    if (this.selected.length > 0) {
      this.removeActivitySessions(this.selected)
    } else {
      this.removeActivitySessions(this.visibleSchedule)
    }
  }

  get removeDisplayedActivitySessionsCount () {
    return _.filter(this.selected.length > 0 ? this.selected : this.visibleSchedule, ({ activitySession }) => !(activitySession.published || activitySession.hasTicketsSold)).length
  }

  removeActivitySessions (scheduleSlots) {
    _.each(scheduleSlots, ({ activitySession }) => {
      if (activitySession.published || activitySession.hasTicketsSold) return

      this.activitySessions.remove(activitySession)
    })
  }

  publishSelectedActivitySessions () {
    _.each(this.selected, ({ activitySession }) => {
      activitySession.published = true
    })
  }

  publishDisplayedActivitySessions () {
    if (this.selected.length > 0) {
      this.publishActivitySessions(this.selected)
    } else {
      this.publishActivitySessions(this.visibleSchedule)
    }
  }

  get publishDisplayedActivitySessionsCount () {
    return _.filter(this.selected.length > 0 ? this.selected : this.visibleSchedule, ({ activitySession }) => !activitySession.published).length
  }

  publishActivitySessions (scheduleSlots) {
    _.each(scheduleSlots, ({ activitySession }) => {
      activitySession.published = true
    })
  }

  unpublishSelectedActivitySessions () {
    _.each(this.selected, ({ activitySession }) => {
      activitySession.published = false
    })
  }

  unpublishDisplayedActivitySessions () {
    if (this.selected.length > 0) {
      this.unpublishActivitySessions(this.selected)
    } else {
      this.unpublishActivitySessions(this.visibleSchedule)
    }
  }

  get unpublishDisplayedActivitySessionsCount () {
    return _.filter(this.selected.length > 0 ? this.selected : this.visibleSchedule, ({ activitySession }) => activitySession.published).length
  }

  unpublishActivitySessions (scheduleSlots) {
    _.each(scheduleSlots, ({ activitySession }) => {
      activitySession.published = false
    })
  }

  finalizeCurrentSelection () {
    _.each(this.fullSchedule, scheduleSlot => {
      scheduleSlot.finalSelection = scheduleSlot.finalSelection || scheduleSlot.currentSelection
      scheduleSlot.currentSelection = false
    })
  }

  validateActivityScheduleSlotGhost (dayNumber, activitySpaceId, left) {
    // Compute time for cursor position
    const cursorOffset = 50 // Draw the schedule slot slightly offset from the cursor so we don't cover up the start time
    const time = date.adjustDate(this.startDate, { hours: START_HOUR, minutes: ((left - cursorOffset) / this.pixelsPerMinute) })
    const days = _.indexOf(this.configuration.currentCircuit.daysOfWeekNumbers, dayNumber)
    const timeZoneOffset = this.getOffsetMinutes(this.configuration.currentSite.timeZone, time) - this.getOffsetMinutes(Intl.DateTimeFormat().resolvedOptions().timeZone, time)

    // Set round to Snap To minutes
    const currentMinutes = timeToMinutes(time)
    const snapTo = this.snapTo || 1
    const roundedMinutes = Math.round(currentMinutes / snapTo) * snapTo

    // Adjust for timezone and Snap To
    const startTime = new IsoDateTime(date.addToDate(time, { days, minutes: -timeZoneOffset + (roundedMinutes - currentMinutes) }))
    const endTime = new IsoDateTime(date.addToDate(startTime, { minutes: this.selectedActivityForPaint.sessionDurationMinutes }))

    if (!this.scheduleSlotGhost) {
      this.scheduleSlotGhost = new ScheduleSlot({
        activitySession: new ActivitySession({
          activity: this.selectedActivityForPaint,
          activitySchedule: this,
          activitySpace: this.configuration.activitySpaces.find({ id: activitySpaceId }),
          startTime,
          endTime
        }),
        ghostSlot: true
      })
    } else if (this.scheduleSlotGhost.activitySession.activitySpace.id !== activitySpaceId) {
      this.scheduleSlotGhost.activitySession.activitySpace = this.configuration.activitySpaces.find({ id: activitySpaceId })
      this.scheduleSlotGhost.activitySession.startTime = startTime
      this.scheduleSlotGhost.activitySession.endTime = endTime
    } else {
      this.scheduleSlotGhost.activitySession.startTime = startTime
      this.scheduleSlotGhost.activitySession.endTime = endTime
    }
  }

  createActivitySessionFromScheduleSlotGhost () {
    if (!this.scheduleSlotGhost) return
    this.activitySessions.add(this.scheduleSlotGhost.createActivitySession())
  }

  destroyScheduleSlotGhost () {
    this.scheduleSlotGhost?.destroy()
    this.scheduleSlotGhost = null
  }

  buildActualSchedule (dataOnly = false) {
    if (!this.activitySessions) return []
    return _.map(this.activitySessions.data, activitySession => dataOnly ? activitySession.id : new ScheduleSlot({ activitySession }))
  }

  buildDisplaySchedule () {
    const schedule = this.scheduleMode === SCHEDULE_MODE_TYPES.PUBLISHED ? _.filter(this.actualSchedule, ({ activitySession }) => activitySession.published) : this.actualSchedule

    if (this.selectedActivityForPaint && this.scheduleSlotGhost && !_.includes(schedule, this.scheduleSlotGhost)) {
      schedule.push(this.scheduleSlotGhost)
    }

    return _.filter(schedule, ({ activitySession }) => {
      if (this.by === ACTIVITY_SCHEDULE_VIEW_TYPES.ACTIVITY_SPACE && this.activitySpaceFilter !== null && activitySession.activitySpace.id !== this.activitySpaceFilter) {
        return false
      }
      if (this.by === ACTIVITY_SCHEDULE_VIEW_TYPES.DAY && this.dayFilter !== null && +activitySession.dayNumber !== this.dayFilter) {
        return false
      }

      return true
    })
  }

  getOffsetMinutes (timeZone = 'UTC', date = new Date()) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }))
    return (tzDate.getTime() - utcDate.getTime()) / 60 / 1000
  }
}
